/**
 * Given a Date obj, remove hours, minutes and seconds, and return the timestamp of it
 * @param date
 */

/**
 * Helper function that returns a given Date/timestamp as a locale representation of it as string
 * in the format <local date string> (<time zone information>)
 */
export function formatDateWithTimezone(value: Date | number | undefined | null): string | undefined {
  if (value === undefined || value === null) {
    return
  }

  const date = value instanceof Date ? value : new Date(value)

  if (Number.isNaN(date.getTime())) {
    return
  }

  return `${date.toLocaleString()} (${Intl.DateTimeFormat().resolvedOptions().timeZone})`
}

export function formatShortDate(value: Date | number | string | undefined | null): string | undefined {
  if (value === undefined || value === null) {
    return
  }

  const date = value instanceof Date ? value : new Date(value)

  if (Number.isNaN(date.getTime())) {
    return
  }

  return date.toLocaleDateString(undefined, { month: 'short', day: '2-digit', year: 'numeric', timeZone: 'UTC' })
}

export function getDateTimestamp(date: Date): number {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime()
}

export function timeSinceInSeconds(timestamp?: number): number | undefined {
  if (!timestamp) {
    return
  }
  const timeDelta = Date.now() - timestamp

  return Math.floor(timeDelta / 1000)
}

export const MAX_VALID_TO_EPOCH = 4294967295 // Max uint32 (Feb 07 2106 07:28:15 GMT+0100)

export const DateFormatOptions: Intl.DateTimeFormatOptions = {
  dateStyle: 'medium',
  timeStyle: 'short',
}

/**
 * Formats a medium date and short time in the runtime's default time zone.
 *
 * @param value - A Date or Unix timestamp in milliseconds.
 * @param locale - A BCP 47 language tag, such as 'en-US'.
 * @returns The localized date and time.
 * @throws {RangeError} If the date is invalid or the locale tag is malformed.
 */
export function formatDateTime(value: Date | number, locale: string): string {
  return new Intl.DateTimeFormat(locale, DateFormatOptions).format(value)
}
